import { ModalWindow } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackAttachFileButtonClick, trackInputTypeClick, trackOnOfferSendButtonClick } from './tracking';
import { AttachFileButton } from '../../components/AttachFileButton';
import { AttachFileModal } from '../../components/AttachFileModal';
import { ChatFileError } from '../../components/ChatFileError';
import { BLOB_ID } from '../../constants/blobId';
import { useReplyTemplateModal } from '../../hooks';
import { useFileChange } from '../../hooks/useFileChange';
import { currentChatOpponentIdSelector, currentOpenChatIDSelector, hostTypeSelector } from '../../selectors';
import { isSearcherSelector } from '../../selectors/isSearcherSelector';
import { EFileError } from '../../types/alert';
import { EHostType } from '../../types/hostType';
import { TFileTypes } from '../../types/messageFiles';
import { useApplicationContext } from '../../utils/applicationContext';
import { getSupportedDocumentMimeTypes, getSupportedImageMimeTypes } from '../../utils/getSupportedDocumentMimeTypes';
import { isDocumentUploadEnabled } from '../../utils/isDocumentUploadEnabled';
import { OffersPickerModalContainer } from '../OffersPickerModalContainer';
import { EReplyTemplateTrackingSource, trackReplyTemplateButtonClick } from '../ReplyTemplateModalContainer/tracking';

export const AttachFileButtonContainer = () => {
  const [isAttachFileModalOpen, setIsAttachFileModalOpen] = React.useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState<boolean>(false);
  const [isSelectOfferModalOpen, setIsSelectOfferModalOpen] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<EFileError>(EFileError.Type);
  const [inputSendType, setInputSendType] = React.useState<TFileTypes | null>(null);

  const chatId = useSelector(currentOpenChatIDSelector) || '';
  const hostType = useSelector(hostTypeSelector);
  const isSearcher = useSelector(isSearcherSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const isIframe = hostType === EHostType.Frame;

  const { config } = useApplicationContext();

  const imageMimeTypes = getSupportedImageMimeTypes(config);
  const documentMimeTypes = getSupportedDocumentMimeTypes(config);
  const documentUploadEnabledValue = isDocumentUploadEnabled(config);

  const isOfferAttachmentEnabled = config.get<boolean>('chats.isOfferAttachmentEnabled') || false;

  const handleInputSendTypeClick = (type: TFileTypes | null) => {
    setInputSendType(type);
    trackInputTypeClick(type, chatId, opponentId);
  };

  const handleToggleAttachFileModal = React.useCallback(() => {
    setIsAttachFileModalOpen(!isAttachFileModalOpen);
  }, [isAttachFileModalOpen]);

  const handleToggleErrorModal = React.useCallback(
    (text?: EFileError) => {
      setIsErrorModalOpen(!isErrorModalOpen);

      if (text) {
        setErrorText(text);
      }
    },
    [isErrorModalOpen],
  );

  const handleToggleOfferModal = React.useCallback(() => {
    setIsSelectOfferModalOpen(!isSelectOfferModalOpen);
    setIsAttachFileModalOpen(!isAttachFileModalOpen);
    trackOnOfferSendButtonClick(chatId, opponentId);
  }, [isSelectOfferModalOpen, isAttachFileModalOpen, chatId, opponentId]);

  const handleClickErrorButton = React.useCallback(() => {
    setIsErrorModalOpen(false);
    handleToggleAttachFileModal();
  }, [handleToggleAttachFileModal]);

  const handleAttachFileButtonClick = React.useCallback(() => {
    handleToggleAttachFileModal();
    trackAttachFileButtonClick(chatId, opponentId);
  }, [chatId, handleToggleAttachFileModal, opponentId]);

  const onCloseAllModals = React.useCallback(() => {
    // чтобы избежать наложения затемнения двух модалок, в момент открытия селектора
    // модалка выбора атачмента закрывает и при закрытии селектора открывает
    // при отправке офферов нужно закрыть все разом
    setIsAttachFileModalOpen(false);
    setIsSelectOfferModalOpen(false);
  }, [setIsAttachFileModalOpen, setIsSelectOfferModalOpen]);

  const { onFileChange } = useFileChange({
    documentMimeTypes,
    imageMimeTypes,
    inputSendType,
    handleToggleErrorModal,
    handleToggleAttachFileModal,
  });

  const {
    isReplyTemplateNewEnabledAttachButton,
    isReplyTemplateNewEnabledAttachModal,
    isReplyTemplateEnabled,
    openReplyTemplateModal,
  } = useReplyTemplateModal();

  const handleReplyTemplateClick = React.useCallback(() => {
    setIsAttachFileModalOpen(false);
    trackReplyTemplateButtonClick({ chatId, opponentId, source: EReplyTemplateTrackingSource.Attachment });
    openReplyTemplateModal(EReplyTemplateTrackingSource.Attachment);
  }, [openReplyTemplateModal, chatId, opponentId]);

  return (
    <>
      <AttachFileButton
        isDisabled={chatId === BLOB_ID}
        isNewEnabled={isReplyTemplateNewEnabledAttachButton}
        onClick={handleAttachFileButtonClick}
      />
      <AttachFileModal
        isOpen={isAttachFileModalOpen}
        imageAccept={imageMimeTypes.join(',')}
        documentAccept={documentMimeTypes?.join(',')}
        isDocumentUploadEnabled={documentUploadEnabledValue}
        isOfferShareAvailable={!isSearcher && !isIframe && isOfferAttachmentEnabled}
        isReplyTemplateEnabled={isReplyTemplateEnabled}
        isReplyTemplateNewEnabled={isReplyTemplateNewEnabledAttachModal}
        onReplyTemplateClick={handleReplyTemplateClick}
        onOfferSendClick={handleToggleOfferModal}
        onChange={onFileChange}
        onClick={handleInputSendTypeClick}
        onClose={handleToggleAttachFileModal}
      />
      <ModalWindow
        open={isErrorModalOpen}
        content={<ChatFileError message={errorText} onClick={handleClickErrorButton} />}
        onClose={handleToggleErrorModal}
        size="M"
        fixed={false}
      />
      <OffersPickerModalContainer isOpen={isSelectOfferModalOpen} onCloseAllModals={onCloseAllModals} />
    </>
  );
};
